import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { APIService, KPI } from '../../@core/api';
import { PerimetersService } from './perimeters.service';

@Injectable()
export class AuditQuestionConfidenceIndicesService {

  public loading$ = new BehaviorSubject<boolean>(false);

  private _auditQuestionConfidenceIndices$: Observable<KPI.AuditQuestionConfidenceIndex[]>;
  private confidenceIndicesByAuditQuestion: { [key: string]: Observable<KPI.AuditQuestionConfidenceIndex>} = {};

  constructor(
    private api: APIService,
    private perimetersService: PerimetersService,
  ) { }

  public get auditQuestionConfidenceIndices$(): Observable<KPI.AuditQuestionConfidenceIndex[]> {
    if (!this._auditQuestionConfidenceIndices$) {
      this._auditQuestionConfidenceIndices$ = this.api.roadmap_audit_question_confidence_indices().pipe(shareReplay(1));
    }
    return this._auditQuestionConfidenceIndices$;
  }

  public getConfidenceIndex$(
    auditQuestion: KPI.AuditQuestion,
    ownershipType?: KPI.OwnershipType,
  ): Observable<KPI.AuditQuestionConfidenceIndex> {
    const key = `${auditQuestion.id}${ownershipType ? '-' + ownershipType.id : ''}`;

    if (!this.confidenceIndicesByAuditQuestion[key]) {
      this.confidenceIndicesByAuditQuestion[key] = combineLatest(
        this.auditQuestionConfidenceIndices$,
        this.perimetersService.perimeters$,
      ).pipe(
        tap(() => this.loading$.next(true)),
        map(([auditQuestionConfidenceIndices, perimeters]) => {
          return {
            auditQuestionConfidenceIndex: auditQuestionConfidenceIndices.find((auditQuestionConfidenceIndex) => {
              return (
                auditQuestionConfidenceIndex.audit_question === auditQuestion.id &&
                (!ownershipType || ownershipType.id === auditQuestionConfidenceIndex.ownership_type)
              );
            }),
            perimeters,
          };
        }),
        filter(({ auditQuestionConfidenceIndex, perimeters }) =>
          auditQuestionConfidenceIndex !== undefined && perimeters.length > 0,
        ),
        switchMap(({ auditQuestionConfidenceIndex, perimeters }) => {
          return this.api.roadmap_audit_question_confidence_indices_confidence_index(
            auditQuestionConfidenceIndex.id,
            {
              buildings: PerimetersService.getBuildingsIds(perimeters).join(','),
            },
          );
        }),
        tap(() => this.loading$.next(false)),
        shareReplay(1),
      );
    }

    return this.confidenceIndicesByAuditQuestion[key];
  }
}
